<!--
*  TTTech nerve-management-system
*  Copyright(c) 2021. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <the-nerve-table
    :id="'iiotUsersTable'"
    :columns="columns"
    :empty-state="$t('users.list.emptyState')"
    :params="params"
    :is-action-menu-enabled="false"
    :is-row-clickable="canAccess('UI_USER:VIEW')"
    store-module="users"
    :delete-message="$t('users.list.deletedItem')"
    @row-clicked="(params) => addNewUser(params.item)"
    @params-changed="(params) => changedParams(params)"
  >
    <template #additional-actions>
      <v-row>
        <v-col id="iiotUsersListUserTypeSelect" cols="5" lg="3" md="4" sm="4">
          <v-select
            v-model="userListFilterOption"
            :items="[
              { name: $t('users.list.all'), value: { type: '' } },
              { name: $t('users.list.local'), value: { type: 'local' } },
              { name: $t('users.list.ldap'), value: { type: 'ldap' } },
            ]"
            :label="$t('users.list.userType')"
            item-value="value.type"
            item-text="name"
            prepend-icon="mdi-filter-outline"
            class="default-search-input"
            attach
            @change="usersFetch()"
          />
        </v-col>
      </v-row>
      <div>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              v-if="canAccess('UI_USER:CREATE') && !isLoggedLdapUser"
              id="iiotUsersListAddNewUserButton"
              fab
              small
              dark
              color="primary"
              class="mr-6"
              v-on="on"
              @click="addNewUser()"
            >
              <v-icon dark> person_add </v-icon>
            </v-btn>
          </template>
          <span>{{ $t('users.list.addUser') }}</span>
        </v-tooltip>
      </div>
    </template>
  </the-nerve-table>
</template>

<script>
import { TheNerveTable } from 'nerve-ui-components';

export default {
  components: {
    TheNerveTable,
  },
  props: {
    page: {
      type: Number,
      default: 0,
    },
    itemsPerPage: {
      type: Number,
      default: 0,
    },
    search: {
      type: String,
      default: '',
    },
    sortBy: {
      type: Array,
      default: () => [],
    },
    sortDesc: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      userListFilterOption: this.type === 'all' ? '' : this.type,
      isLoggedLdapUser: false,
      params: {},
    };
  },
  computed: {
    columns() {
      if (this.canAccess('UI_USER:DELETE') && !this.isLoggedLdapUser) {
        return [
          {
            text: this.$t('users.list.firstName'),
            value: 'firstName',
          },
          {
            text: this.$t('users.list.lastName'),
            value: 'lastName',
          },
          {
            text: this.$t('users.list.email'),
            value: 'contact.contact',
          },
          {
            text: this.$t('users.list.type'),
            value: 'type',
            sortable: false,
          },
          {
            text: this.$t('users.list.created'),
            value: 'created',
            sortable: false,
          },
          {
            text: this.$t('users.list.userRoles'),
            value: 'roles.prettyRoleName',
            sortable: false,
          },
          {
            text: this.$t('defaultTable.action'),
            value: '',
            width: '0%',
            sortable: false,
            component: {
              sfc: () => import('@/components/users/UserListTableAction.vue'),
              props: {
                iconsAction: ['Delete'],
                fetchAction: () => this.filterBy,
              },
            },
          },
        ];
      }
      return [
        {
          text: this.$t('users.list.firstName'),
          value: 'firstName',
        },
        {
          text: this.$t('users.list.lastName'),
          value: 'lastName',
        },
        {
          text: this.$t('users.list.email'),
          value: 'contact.contact',
        },
        {
          text: this.$t('users.list.type'),
          value: 'type',
          sortable: false,
        },
        {
          text: this.$t('users.list.created'),
          value: 'created',
          sortable: false,
        },
        {
          text: this.$t('users.list.userRoles'),
          value: 'roles.prettyRoleName',
          sortable: false,
        },
      ];
    },
  },
  created() {
    this.params = {
      page: this.page || 1,
      itemsPerPage: this.itemsPerPage || 10,
      search: this.search || '',
      sortBy: this.sortBy || [],
      sortDesc: this.sortDesc || [],
      filterBy: this.type === 'all' ? '' : this.type,
    };
    if (JSON.parse(localStorage.getItem('userDetails')) && JSON.parse(localStorage.getItem('userDetails')).ldap) {
      this.isLoggedLdapUser = true;
    }
  },
  methods: {
    async filterBy() {
      try {
        await this.$store.dispatch('users/fetch', this.params);
      } catch (e) {
        this.$log.debug(e);
      }
    },
    changedParams(params) {
      this.params = params;
      this.$router
        .push({
          name: 'Users',
          query: {
            page: params.page,
            itemsPerPage: params.itemsPerPage,
            search: params.search || undefined,
            sortBy: params.sortBy ? params.sortBy[0] : [],
            sortDesc: params.sortDesc ? params.sortDesc[0] : [],
            type: params.filterBy === '' ? 'all' : params.filterBy,
          },
        })
        .catch(() => {});
    },
    usersFetch() {
      this.params.filterBy = this.userListFilterOption;
      this.params.page = 1;
      this.$store.dispatch('users/fetch', this.params);
    },
    addNewUser(item) {
      if (item && item.id) {
        this.$router.push({ name: 'Add Edit User', params: { id: item.id } });
        return;
      }
      this.$router.push({ name: 'Add Edit User', params: { id: 'new' } });
    },
  },
};
</script>
